import { defineStore } from "pinia";
import { getUserTimezone } from "../../Common/dateTimeUtils";
import { EventType } from "../../Common/types";
import { WizardCategoriesSetupMethod } from "../../Api/events";
import localStorage from "../../Common/store";

type WizardDraft = {
    currentStep: 1 | 2 | 3 | 4 | 5;
    timezone: string;
    fullName: string;
    eventTypes: EventType[];
    startDate: string;
    endDate: string;
    country: string | null;
    place: string;
    contactEmail: string;
    contactPhone: string;
    registrationStages: { durationDays: number }[];
    registrationOpenDate: string | null;
    registrationConfigured?: boolean;
    categoriesSetupMethod: WizardCategoriesSetupMethod | null;
    categoriesTemplateNames: string[] | null;
    categoriesCategoryPaths: string[] | null;
    categoriesSourceEventId: number | null;
    categoriesStagedPreviewCount: number | null;
    categoriesStagedSummaryLabel: string | null;
    savedAt: number;
};

const DRAFT_TTL_MS = 48 * 60 * 60 * 1000;

function draftKey(federationId: string) {
    return `wizard.draft.${federationId}`;
}

export const useWizardStore = defineStore("createEventWizard", {
    state: () => ({
        federationId: "",
        organizerPhone: null as string | null,
        timezone: getUserTimezone(),

        currentStep: 1 as 1 | 2 | 3 | 4 | 5,
        saving: false,
        validationErrors: {} as Record<string, string[]>,

        // Step 1
        fullName: "",
        eventTypes: [] as EventType[],
        startDate: "",
        endDate: "",

        // Step 2
        country: null as string | null,
        place: "",
        contactEmail: "",
        contactPhone: "",

        // Step 3
        registrationStages: [{ durationDays: 30 }] as { durationDays: number }[],
        registrationOpenDate: null as string | null,
        registrationConfigured: false,

        // Step 4
        logoFile: null as File | null,
        backgroundFile: null as File | null,
        logoPreviewUrl: null as string | null,
        backgroundPreviewUrl: null as string | null,

        // Step 5
        categoriesSetupMethod: null as WizardCategoriesSetupMethod | null,
        categoriesTemplateNames: null as string[] | null,
        categoriesCategoryPaths: null as string[] | null,
        categoriesSourceEventId: null as number | null,
        categoriesStagedPreviewCount: null as number | null,
        categoriesStagedSummaryLabel: null as string | null,
        // Fetched fact (not user input) — not persisted in WizardDraft.
        hasPriorEventsForCopy: null as boolean | null,
    }),
    actions: {
        persistDraft() {
            localStorage.setObject<WizardDraft>(draftKey(this.federationId), {
                currentStep: this.currentStep,
                timezone: this.timezone,
                fullName: this.fullName,
                eventTypes: this.eventTypes,
                startDate: this.startDate,
                endDate: this.endDate,
                country: this.country,
                place: this.place,
                contactEmail: this.contactEmail,
                contactPhone: this.contactPhone,
                registrationStages: this.registrationStages,
                registrationOpenDate: this.registrationOpenDate,
                registrationConfigured: this.registrationConfigured,
                categoriesSetupMethod: this.categoriesSetupMethod,
                categoriesTemplateNames: this.categoriesTemplateNames,
                categoriesCategoryPaths: this.categoriesCategoryPaths,
                categoriesSourceEventId: this.categoriesSourceEventId,
                categoriesStagedPreviewCount: this.categoriesStagedPreviewCount,
                categoriesStagedSummaryLabel: this.categoriesStagedSummaryLabel,
                savedAt: Date.now(),
            });
        },
        loadDraft() {
            const draft = localStorage.getObject<WizardDraft>(draftKey(this.federationId));
            if (!draft) {
                return;
            }
            if (Date.now() - draft.savedAt > DRAFT_TTL_MS) {
                this.clearDraft();
                return;
            }
            this.currentStep = draft.currentStep;
            this.timezone = draft.timezone;
            this.fullName = draft.fullName;
            this.eventTypes = draft.eventTypes;
            this.startDate = draft.startDate;
            this.endDate = draft.endDate;
            this.country = draft.country;
            this.place = draft.place;
            this.contactEmail = draft.contactEmail;
            this.contactPhone = draft.contactPhone;
            this.registrationStages = draft.registrationStages;
            this.registrationOpenDate = draft.registrationOpenDate;
            this.registrationConfigured = draft.registrationConfigured ?? draft.currentStep > 3;
            this.categoriesSetupMethod = draft.categoriesSetupMethod;
            this.categoriesTemplateNames = draft.categoriesTemplateNames;
            this.categoriesCategoryPaths = draft.categoriesCategoryPaths;
            this.categoriesSourceEventId = draft.categoriesSourceEventId;
            this.categoriesStagedPreviewCount = draft.categoriesStagedPreviewCount;
            this.categoriesStagedSummaryLabel = draft.categoriesStagedSummaryLabel;
        },
        clearDraft() {
            localStorage.delete(draftKey(this.federationId));
        },
    },
});
